import { RiCompass4Line, Stack } from 'component-library';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { ConservationExplainerWell, NCCardSectionHeader } from '@/components/NCCard/NCCard';
import { useGetConservationBenchmarks } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetConservationBenchmarks';
import { useGetExplainerFallbackText } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetExplainerFallbackText';
import { useGetTrendLabel } from '@/pages/shared/projects/pages/project/pages/conservation/BU/hooks/useGetTrendLabel';
import { getBenchmarkForConservationFact } from '@/pages/shared/projects/pages/project/pages/conservation/BU/utils/getBenchmarkForConservationFact';
import {
  FactCapsule,
  FactCapsuleTrend,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useLatestAnalysisDatestamp } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useLatestAnalysisDatestamp';

export const StatsTile = ({
  baselineFact,
  latestFact,
  changeFact,
}: {
  baselineFact: ReturnType<typeof useFact<number>>;
  latestFact: ReturnType<typeof useFact<number>>;
  changeFact: ReturnType<typeof useFact<number>>;
}) => {
  const { t } = useTranslation();

  const currentYear = useFact<number>('r1_conservation_current_year');
  const baselineYear = useFact<number>('r1_conservation_indicator_species_baseline_year');

  const latestAnalysisDatestamp = useLatestAnalysisDatestamp(
    latestFact?.measured_at ? { facts: latestFact } : undefined,
  );

  const benchmarks = useGetConservationBenchmarks();
  const projectBenchmark = getBenchmarkForConservationFact({
    fact: latestFact,
    benchmarks,
  });

  const trendLabel = useGetTrendLabel({
    trendChangeFact: 'r1_conservation_indicator_species_change_percentage',
  });

  const customExplainerText = useFact<string>('r1_conservation_indicator_species_historic_explainer');
  const { benchmarkText, trendText } = useGetExplainerFallbackText({
    benchmark: {
      label: projectBenchmark,
      value: latestFact.value,
    },
    trend: {
      label: trendLabel,
      value: changeFact.value,
    },
    indicator: 'indicatorSpecies',
  });

  const fallbackExplainerText =
    benchmarkText && trendText ? `${benchmarkText}\n\n${trendText}` : benchmarkText || trendText || null;
  return (
    <Stack spacing={10} className='px-6' data-testid='indicator-species-stats-tile'>
      <Stack spacing={6}>
        <NCCardSectionHeader
          title={t('shared.ncaDetail.indicatorSpecies.evolution.statsHeading')}
          popover={{
            title: t('shared.ncaDetail.indicatorSpecies.explainers.indicatorSpecies.title'),
            body: t('shared.ncaDetail.indicatorSpecies.explainers.indicatorSpecies.body'),
          }}
          supplementaryInfo={latestAnalysisDatestamp}
        />
        <Stack spacing={16} className='flex-wrap md:flex-row'>
          <FactCapsule
            label={t('shared.ncaDetail.indicatorSpecies.evolution.explainers.baselineValue.title')}
            fact={baselineFact}
            capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.indicatorSpecies.evolution.explainers.baselineValue.title'),
              body: t('shared.ncaDetail.indicatorSpecies.evolution.explainers.baselineValue.body', {
                year: baselineYear?.value,
              }),
              popoverTriggerProps: {
                'data-testid': 'indicator-species-baseline-value-popover',
              },
            }}
          />
          <FactCapsule
            label={t('shared.ncaDetail.indicatorSpecies.evolution.explainers.averageValue.title', {
              year: currentYear?.value,
            })}
            fact={latestFact}
            trend={<FactCapsuleTrend trend={changeFact} />}
            capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
            infoPopoverProps={{
              title: t('shared.ncaDetail.indicatorSpecies.evolution.explainers.averageValue.title', {
                year: currentYear?.value,
              }),
              body: t('shared.ncaDetail.indicatorSpecies.evolution.explainers.averageValue.body', {
                year: currentYear?.value,
              }),
              popoverTriggerProps: {
                'data-testid': 'indicator-species-latest-analysis-popover',
              },
            }}
          />
        </Stack>
      </Stack>
      <ConservationExplainerWell
        benchmark={projectBenchmark}
        trendLabel={trendLabel}
        title={t('global.whatDoesThisMean')}
        body={
          customExplainerText?.value ? (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(customExplainerText.value),
              }}
            />
          ) : (
            <span className='whitespace-pre-wrap'>{fallbackExplainerText}</span>
          )
        }
      />
    </Stack>
  );
};
